import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'semantic-ui-react';
import { Button, Field, Header, Input, Message, Modal, Toast } from 'react-frontier';
import { bindFormChange, formatCreditCard, getBankParticipants, validateClabeParticipant } from '../SiteUtil';
import { clabe } from 'clabe-validator';
import { BankAccount, BankAccountForm, BankAccountOwnerType, DepositType, SetLoading } from '../SiteClasses';
import API from '../API';
import Validator from '../Validator';

interface ModalProps {
	open: boolean,
	onClose: ()=>void,
	onCreate: (account: BankAccount)=>void,
	worker_id?: number,
	user_id?: number,
}

var BankAccountModal = (props: ModalProps)=>{
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [form, setForm] = useState<BankAccountForm>(null);
	var participants = useMemo(getBankParticipants, []);

	useEffect(()=>{
		if(props.open){
			setForm({
					worker_id: props.worker_id,
					type: null,
					account_owner: '',
					clabe: '',
					card_number: '',
					bank_participant: null,
					rfc: '',
					postal_code: '',
					owner_type: null,
			});
		}
	}, [props.open]);

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			type: [{ rule: 'empty', prompt: 'Favor de seleccionar el tipo de cuenta bancaria.' }],
			account_owner: [{ rule: 'minLength', params: [3], prompt: `Favor de ingresar el propietario de ${form.type===DepositType.CARD ? 'la tarjeta' : 'la cuenta'}` }],
			...(form.owner_type===BankAccountOwnerType.FISICA ? {
					postal_code: [{ rule: 'minLength', params: [5] }, 'number'],
			} : {}),
			...(form.type===DepositType.CARD ? {            
					card_number: [{ rule: 'creditcard' }],
					bank_participant: [{ rule: 'empty', prompt: 'Favor de seleccionar el banco de la tarjeta.' }],

			} : form.type===DepositType.SPEI ? {
					clabe: [{ 
						rule: v=>{
							var val = clabe.validate(v);
							return val && val.ok;
						}, 
						prompt: 'La CLABE no es válida.' 
					}, {
						skipEmpty: true,
						rule: validateClabeParticipant,
						prompt: 'La CLABE no pertenece a un banco válido.'
					}]                
			} : {})
		});

		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);

		var apiCall = props.worker_id
			? API.addWorkerBankAccount(props.worker_id, form)
			: API.createUserBankAccount(form, props.user_id);

		apiCall.then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Cuenta agregada!')
			
			props.onCreate(res.data);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado creando la cuenta bancaria (LCL-BKACCCT-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onDataChange = bindFormChange(form, setForm);
	
	return <Modal open={props.open} onClose={props.onClose} size='tiny'>
		<Modal.Header>Agregar cuenta bancaria</Modal.Header>
		<Modal.Content>
			{!form ? (
					<Header loading text='Cargando' />
			) : <>
					<Field label='Tipo de propietario'>
						<Dropdown selection placeholder='Tipo de propietario' value={form.owner_type} onChange={(e, data)=>{
							setForm({
									...form,
									owner_type: (data.value as number),
									postal_code: ''
							});
						}} options={[
							{ text: 'Persona fisica', value: BankAccountOwnerType.FISICA },
							{ text: 'Persona moral', value: BankAccountOwnerType.MORAL }
						]} />
					</Field>
					<Field label='Tipo de cuenta'>
						<Dropdown selection placeholder='Tipo de cuenta' value={form.type} onChange={onDataChange('type', true)} options={[
							{ text: 'Transferencia bancaria (SPEI)', value: DepositType.SPEI },
							{ text: 'Tarjeta bancaria', value: DepositType.CARD }
						]} />
					</Field>
					{form.type===DepositType.SPEI ? (
						<>
							<Input label='Propietario de cuenta' value={form.account_owner} onChange={onDataChange('account_owner')} />
							<Field amount={2}>
									<Input label='CLABE' value={form.clabe} onChange={onDataChange('clabe')} />
									<Input label='Banco' readonly value={clabe.validate(form.clabe).bank} />
							</Field>
						</>
					) : form.type===DepositType.CARD ? (
						<>
							<Input label='Nombre en la tarjeta' value={form.account_owner} onChange={onDataChange('account_owner')} />
							<Input label="Número de la tarjeta" value={form.card_number} onChange={onDataChange('card_number')} valueFormat={formatCreditCard} maxLength={16+3} />
							<Field label='Banco'>
									<Dropdown search selection selectOnBlur={false} value={form.bank_participant} onChange={onDataChange('bank_participant', true)} placeholder='Banco de la tarjeta' options={participants.map((a,i)=>({
										text: a.bank_name,
										value: a.participant,
										key: `${a.participant}-${i}`
									}))} />
							</Field>
							<Message type='info' style={{ marginBottom: 10, marginTop: 15 }}>
									<ul>
										<li>Para evitar cualquier tipo de problema, <b>favor de ingresar solo tarjetas de débito</b>.</li>
										<li>Por el momento solo podemos hacer transferencias a <b>tarjetas de instituciones bancarias mexicanas</b>.</li>
									</ul>
							</Message>
						</>
					) : null}
					<Input label='RFC' onChange={onDataChange('rfc')} value={form.rfc} />
					{form.owner_type===BankAccountOwnerType.FISICA && (
						<Input label='Código postal' onChange={onDataChange('postal_code')} value={form.postal_code} />
					)}
					<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
			</>}
		</Modal.Content>
		<Modal.Actions>
			<Button basic text='Cerrar' onClick={props.onClose} />
			<Button color="black" text='Guardar' onClick={submit} />
		</Modal.Actions>
	</Modal>
}

export default BankAccountModal;